import { forwardRef, type ReactNode } from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

/**
 * RwButton — the DS Button (rw-ds.css `.rwb` contract + MUI-BRIDGE §Button).
 *
 * Renders a native `<button>` (or `<a>` when `href` is set) carrying the `.rwb`
 * classes, so it is pixel-exact to the DS with no MUI Button base-style bleed.
 * `sx` is supported (via `Box`) for layout only — never for on-DS visual
 * overrides. Ripple/elevation are N/A (no MUI Button). Focus ring, disabled,
 * loading spinner, and open-state all come from `.rwb` CSS.
 *
 * DS-first API: `tier` / `size` (xs|sm|md|lg) / `destructive` / `tone` / `iconOnly`
 * / `loading` / `flush` / `startIcon` / `endIcon`.
 *
 * MUI-compat (for drop-in migration): `variant` (contained→primary,
 * outlined→ghost, text→plain), `color` (error→destructive; success/warning/info
 * →tone), and MUI sizes (small|medium|large). Explicit `tier`/`size`/`tone`/
 * `destructive` win over the mapped MUI props.
 */
export type RwButtonTier = 'primary' | 'secondary' | 'ghost' | 'plain' | 'plain-neutral';
/**
 * `xs` (24px) is CRM-LOCAL, not canonical — `.rwb--xs` lives in the CRM's
 * `src/styles/app/button-xs.css`, which carries the promotion note and the scope clause.
 * SCOPE: only for an affordance sitting inline among chips or running text. Not a toolbar
 * size, not a form size, not a general "small button". Anything else stays `sm` or larger.
 */
export type RwButtonSize = 'xs' | 'sm' | 'md' | 'lg';
export type RwButtonTone = 'success' | 'warning' | 'information';

type MuiVariant = 'contained' | 'outlined' | 'text';
type MuiColor = 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' | 'default';
type MuiSize = 'small' | 'medium' | 'large';

export interface RwButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  tier?: RwButtonTier;
  size?: RwButtonSize | MuiSize;
  destructive?: boolean;
  tone?: RwButtonTone;
  iconOnly?: boolean;
  loading?: boolean;
  flush?: boolean;
  fullWidth?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  sx?: SxProps<Theme>;
  href?: string;
  /** MUI-compat; mapped to `tier` when `tier` is not given. */
  variant?: MuiVariant;
  /** MUI-compat; mapped to `destructive`/`tone` when those are not given. */
  color?: MuiColor;
  /** MUI `edge` — negative inline margin; mapped to the DS `flush` treatment. */
  edge?: 'start' | 'end' | false;
  /** Polymorphic root (e.g. a router `Link`); forwarded to the underlying `Box`. */
  component?: React.ElementType;
  /** Forwarded when `component` is a router `Link`. */
  to?: string;
  target?: string;
  rel?: string;
}

const VARIANT_TO_TIER: Record<MuiVariant, RwButtonTier> = {
  contained: 'primary',
  outlined: 'ghost',
  text: 'plain'
};

const MUI_SIZE_TO_DS: Record<MuiSize, RwButtonSize> = {
  small: 'sm',
  medium: 'md',
  large: 'lg'
};

/** MUI-compat `color` → DS `tone`. A lookup, not a ternary chain (`no-nested-ternary`); `error` is
 *  absent because it maps to `destructive`, which is a modifier rather than a tone. */
const MUI_COLOR_TO_TONE: Partial<Record<MuiColor, RwButtonTone>> = {
  success: 'success',
  warning: 'warning',
  info: 'information'
};

function resolveSize(size: RwButtonProps['size']): RwButtonSize {
  if (size === 'xs' || size === 'sm' || size === 'md' || size === 'lg') return size;
  if (size && size in MUI_SIZE_TO_DS) return MUI_SIZE_TO_DS[size as MuiSize];
  return 'md';
}

const RwButton = forwardRef<HTMLButtonElement, RwButtonProps>(function RwButton(
  {
    tier,
    size = 'md',
    destructive,
    tone,
    iconOnly = false,
    loading = false,
    flush = false,
    fullWidth = false,
    startIcon,
    endIcon,
    className,
    sx,
    href,
    variant,
    color,
    edge,
    component,
    disabled,
    type,
    children,
    ...rest
  },
  ref
) {
  const isLink = Boolean(href);
  const isPlainButton = !component && !isLink;
  // No tier + no variant → 'plain' (MUI Button's default variant is "text").
  const resolvedTier: RwButtonTier = tier ?? (variant ? VARIANT_TO_TIER[variant] : 'plain');
  const resolvedDestructive = destructive ?? color === 'error';
  const resolvedTone: RwButtonTone | undefined = tone ?? (color ? MUI_COLOR_TO_TONE[color] : undefined);
  const resolvedSize = resolveSize(size);

  const classes = [
    'rwb',
    `rwb--${resolvedTier}`,
    `rwb--${resolvedSize}`,
    resolvedDestructive ? 'rwb--destructive' : '',
    resolvedTone ? `rwb--${resolvedTone}` : '',
    iconOnly ? 'rwb--icon-only' : '',
    flush || edge ? 'rwb--flush' : '',
    loading ? 'is-loading' : '',
    className ?? ''
  ]
    .filter(Boolean)
    .join(' ');

  // Only forward `href` when it's actually set (the plain `<a href>` case). When a
  // polymorphic `component` (e.g. router `Link`) is used with `to` instead, an
  // explicit `href={undefined}` key still lands in the props object handed to that
  // component — and since `Link` spreads its own `...rest` after computing
  // `href={to}`, that stray `href: undefined` clobbers the real href.
  const hrefProps = href ? { href } : {};

  return (
    <Box
      component={component ?? (isLink ? 'a' : 'button')}
      ref={ref}
      {...hrefProps}
      className={classes}
      disabled={isPlainButton ? disabled || loading : undefined}
      aria-busy={loading || undefined}
      aria-disabled={!isPlainButton && (disabled || loading) ? true : undefined}
      type={isPlainButton ? (type ?? 'button') : undefined}
      sx={{
        // Icons are MUI SvgIcons here (not the icon font) — size them to the DS 16px;
        // colour inherits currentColor from the tier.
        '& .MuiSvgIcon-root': { fontSize: 'var(--size-16)' },
        ...(fullWidth ? { width: '100%' } : {}),
        ...sx
      }}
      {...rest}
    >
      {startIcon}
      {children}
      {endIcon}
    </Box>
  );
});

export default RwButton;
// Named alias — some call sites import `{ RwButton }` directly from this file (pre-dating
// the barrel convention below); kept so this port doesn't break every existing consumer.
export { RwButton };
